// Pièces du dossier : versions, statut de lecture (dont « illisible » et son détail), provenance, dates ; ajout et nouvelle version.
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, FilePlus2, Upload } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Chargement, EtatErreur, EtatVide, Panneau, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutDocument } from '../../components/statuts.tsx';
import { DossierCadre } from './DossierCadre.tsx';
import { DepotPieces, envoyerPieces, TYPES_PIECE, useSynthese, type PieceADeposer } from './commun.tsx';

interface Version { id: string; version_label: string; status: string; status_detail: string | null; file_name: string; size_bytes: number; page_count: number | null; created_at: string; collected_at: string }
interface Piece { id: string; title: string; doc_type: string; provenance: string | null; demo: boolean; created_at: string; cree_par: string | null; versions: Version[] }

export function PagePieces() {
  const { id = '' } = useParams();
  const { peut } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({
    queryKey: ['etudes-pieces', id], queryFn: () => api.get<{ pieces: Piece[]; enCours: number }>(`/api/dossiers/${id}/pieces`),
    refetchInterval: (x) => ((x.state.data?.enCours ?? 0) > 0 ? 1500 : false),
  });
  const synthese = useSynthese(id);
  const [aDeposer, setADeposer] = useState<PieceADeposer[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const ecrire = peut('dossier.ecrire');

  const rafraichir = async () => {
    await qc.invalidateQueries({ queryKey: ['etudes-pieces', id] });
    await qc.invalidateQueries({ queryKey: ['etudes-synthese', id] });
    await qc.invalidateQueries({ queryKey: ['dossier', id] });
  };
  const importer = async (pieces: PieceADeposer[], documentId?: string) => {
    setEnvoi(true);
    try {
      const r = await envoyerPieces(id, pieces, documentId);
      const echecs = r.filter((x) => x.erreur);
      const doublons = r.filter((x) => x.doublon);
      if (echecs.length) notifier(`Non importé : ${echecs.map((x) => `${x.nom} (${x.erreur})`).join(' ; ')}`, true);
      if (doublons.length) notifier(`${pluriel(doublons.length, 'fichier déjà présent', 'fichiers déjà présents')} dans ce dossier : non réimporté${doublons.length > 1 ? 's' : ''}.`);
      if (r.length > echecs.length + doublons.length) notifier('Pièces importées : lecture puis analyse automatiques.');
      if (!documentId) setADeposer([]);
      await rafraichir();
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnvoi(false); }
  };

  return (
    <DossierCadre dossierId={id} actif="pieces">
      <div className="pile-l">
        {synthese.data && (synthese.data.pieces.absentes.length > 0 || synthese.data.pieces.illisibles.length > 0) && (
          <Alerte ton="danger" titre="Pièces absentes ou illisibles">
            <ul className="et-liste-simple">
              {synthese.data.pieces.absentes.map((a) => <li key={a.cle}><strong>{a.titre}</strong> — {a.description}</li>)}
              {synthese.data.pieces.illisibles.map((i) => <li key={i.versionId}><strong>{i.titre}</strong> — {i.description}</li>)}
            </ul>
          </Alerte>
        )}
        {q.data && q.data.enCours > 0 && <Alerte ton="info" titre={`${pluriel(q.data.enCours, 'pièce')} en cours de lecture`}>Le dossier sera analysé automatiquement ensuite.</Alerte>}
        {q.isLoading && <Chargement />}
        {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
        {q.data && q.data.pieces.length === 0 && <Panneau><EtatVide icone={<FileText />} titre="Aucune pièce" texte="Déposez la demande du client, le CCTP, les plans, la DPGF ou les devis existants." /></Panneau>}
        {q.data && q.data.pieces.length > 0 && (
          <Panneau serre titre={pluriel(q.data.pieces.length, 'pièce')}>
            <ul className="et-pieces">
              {q.data.pieces.map((p) => <LignePiece key={p.id} piece={p} ecrire={ecrire} envoi={envoi} nouvelleVersion={(pp) => importer(pp, p.id)} />)}
            </ul>
          </Panneau>
        )}
        {ecrire && (
          <Panneau titre="Ajouter des pièces">
            <div className="pile">
              <DepotPieces pieces={aDeposer} changer={setADeposer} libelle="Nouvelles pièces" />
              {aDeposer.length > 0 && (
                <div className="groupe-btn"><Bouton variante="principal" icone={<Upload aria-hidden />} chargement={envoi} onClick={() => importer(aDeposer)}>Importer {pluriel(aDeposer.length, 'pièce')}</Bouton></div>
              )}
            </div>
          </Panneau>
        )}
      </div>
    </DossierCadre>
  );
}

function LignePiece({ piece: p, ecrire, envoi, nouvelleVersion }: { piece: Piece; ecrire: boolean; envoi: boolean; nouvelleVersion: (p: PieceADeposer[]) => void }) {
  const entree = useRef<HTMLInputElement>(null);
  const [libelle, setLibelle] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const actuelle = p.versions[0];
  return (
    <li className="et-piece">
      <div className="et-piece-entete">
        <FileText aria-hidden className="discret" />
        <div className="pile" style={{ gap: 2, minWidth: 0, flex: 1 }}>
          <strong className="et-coupe">{p.title}</strong>
          <span className="petit discret">{TYPES_PIECE[p.doc_type] ?? p.doc_type}{p.provenance ? ` — ${p.provenance}` : ''}{p.cree_par ? ` — importée par ${p.cree_par}` : ''}</span>
        </div>
        {p.demo && <Badge ton="ambre">Démo</Badge>}
        {ecrire && <Bouton compact icone={<FilePlus2 aria-hidden />} onClick={() => entree.current?.click()} disabled={envoi}>Nouvelle version</Bouton>}
        <input ref={entree} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.csv,.txt" onChange={(e) => { setFichier(e.target.files?.[0] ?? null); e.target.value = ''; }} />
      </div>
      {fichier && (
        <div className="et-nouvelle-version">
          <span className="petit">Nouvelle version : <span className="mono">{fichier.name}</span></span>
          <label className="champ"><span className="champ-libelle">Libellé de version</span>
            <input className="saisie" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="indice B, rév. 3…" /></label>
          <div className="groupe-btn">
            <Bouton variante="principal" compact chargement={envoi} onClick={() => { nouvelleVersion([{ fichier, type: p.doc_type, provenance: p.provenance ?? '', versionLabel: libelle }]); setFichier(null); setLibelle(''); }}>Importer la version</Bouton>
            <Bouton compact onClick={() => setFichier(null)}>Annuler</Bouton>
          </div>
        </div>
      )}
      <ul className="et-versions" aria-label={`Versions de ${p.title}`}>
        {p.versions.map((v) => (
          <li key={v.id} className={v === actuelle ? '' : 'et-version-ancienne'}>
            <span className="et-version-ligne">
              <span className="mono petit">{v.version_label}</span>
              <BadgeStatutDocument statut={v.status} />
              <span className="tres-petit discret">{v.file_name} — {Math.max(1, Math.round(v.size_bytes / 1024))} Ko{v.page_count ? ` — ${pluriel(v.page_count, 'page')}` : ''} — reçue le {dateHeure(v.collected_at)}</span>
              {['valide', 'a_valider', 'obsolete'].includes(v.status) && <Link className="btn btn-compact btn-fantome" to={`/documentation/lecteur/${v.id}`}>Ouvrir</Link>}
            </span>
            {v.status_detail && <span className={`petit ${v.status === 'illisible' ? 'et-texte-danger' : 'et-texte-ambre'}`}>{v.status_detail}</span>}
          </li>
        ))}
      </ul>
    </li>
  );
}
